/**
 * Abstract base class for all streaming platform services
 * 
 * This class provides common functionality and enforces a consistent interface
 * across all platform implementations (TikTok, Twitch, YouTube).
 * Ported from beemi-react-native for web compatibility.
 */

import {
  StreamingPlatform,
  ConnectionState,
  StreamingComment,
  StreamingGift,
  StreamingLike,
  StreamingMetrics,
  StreamingRoomInfo,
  StreamValidationResult,
  PlatformConnectionOptions,
  StreamingCallbacks,
  PlatformError,
  ConnectionStatus
} from '../../../types/streaming';

export abstract class StreamingPlatformService {
  protected platform: StreamingPlatform;
  protected connectionState: ConnectionState = 'disconnected';
  protected callbacks: StreamingCallbacks = {};
  protected reconnectAttempts = 0;
  protected maxReconnectAttempts = 5;
  protected reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  protected connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  protected isCleanedUp = false;
  protected roomInfo: StreamingRoomInfo | null = null;
  protected streamMetrics: StreamingMetrics;

  constructor(platform: StreamingPlatform) {
    this.platform = platform;
    this.streamMetrics = {
      platform,
      viewerCount: 0,
      likeCount: 0,
      giftCount: 0,
      memberCount: 0,
      shareCount: 0,
      commentCount: 0
    };
  }

  // Abstract methods that each platform must implement
  abstract connect(options: PlatformConnectionOptions): Promise<void>;
  abstract disconnect(): void;
  abstract validateStreamRequirements(): StreamValidationResult;

  /**
   * Set callback functions for streaming events
   */
  public setCallbacks(callbacks: StreamingCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if service is connected
   */
  public isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * Get platform identifier
   */
  public getPlatform(): StreamingPlatform {
    return this.platform;
  }

  /**
   * Get current stream metrics
   */
  public getStreamMetrics(): StreamingMetrics {
    return { ...this.streamMetrics };
  }

  /**
   * Get room/stream information
   */
  public getRoomInfo(): StreamingRoomInfo | null {
    return this.roomInfo;
  }

  /**
   * Get connection status with metadata
   */
  public getConnectionStatus(): ConnectionStatus {
    return {
      state: this.connectionState,
      platform: this.platform,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  /**
   * Check if service has been cleaned up
   */
  public isServiceCleanedUp(): boolean {
    return this.isCleanedUp;
  }

  /**
   * Clean up all resources and timers
   */
  public cleanup(): void {
    this.isCleanedUp = true;
    this.clearTimeouts();
    this.disconnect();
    this.callbacks = {};
    this.roomInfo = null;
    this.resetMetrics();
  }

  /**
   * Handle reconnection logic (common across platforms)
   */
  protected handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts || this.isCleanedUp) {
      this.callbacks.onError?.(new Error('Max reconnection attempts reached'));
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    this.connectionState = 'reconnecting';
    this.callbacks.onConnectionStatus?.(`Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(async () => {
      if (!this.isCleanedUp && this.callbacks.onConnect) {
        try {
          await this.attemptReconnect();
        } catch (error) {
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.callbacks.onError?.(error as Error);
          } else {
            this.handleReconnection();
          }
        }
      }
    }, delay);
  }

  /**
   * Platform-specific reconnection logic (override in subclasses)
   */
  protected async attemptReconnect(): Promise<void> {
    throw new Error('Reconnection not implemented for this platform');
  }

  /**
   * Set connection state and notify callbacks
   */
  protected setConnectionState(state: ConnectionState): void {
    console.log(`🔄 [${this.platform}] setConnectionState:`, state);
    this.connectionState = state;
    
    switch (state) {
      case 'connected':
        this.reconnectAttempts = 0;
        this.callbacks.onConnect?.();
        break;
      case 'disconnected':
        this.callbacks.onDisconnect?.();
        break;
      case 'error':
        // Error handling is done by the specific error callback
        break;
    }
  }

  /**
   * Clear all timeouts
   */
  protected clearTimeouts(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  /**
   * Reset stream metrics to initial values
   */
  protected resetMetrics(): void {
    this.streamMetrics = {
      platform: this.platform,
      viewerCount: 0,
      likeCount: 0,
      giftCount: 0,
      memberCount: 0,
      shareCount: 0
    };
  }

  /**
   * Update stream metrics and notify callbacks
   */
  protected updateMetrics(updates: Partial<StreamingMetrics>): void {
    this.streamMetrics = {
      ...this.streamMetrics,
      ...updates,
      platform: this.platform
    };
    
    this.callbacks.onMetricsUpdate?.(this.streamMetrics);
  }

  /**
   * Handle incoming comment (normalized from platform-specific data)
   */
  protected handleComment(comment: StreamingComment): void {
    if (comment.comment.trim()) {
      this.callbacks.onComment?.(comment);
    }
  }

  /**
   * Handle incoming gift (normalized from platform-specific data)
   */
  protected handleGift(gift: StreamingGift): void {
    this.updateMetrics({
      giftCount: (this.streamMetrics.giftCount || 0) + gift.giftCount
    });
    
    this.callbacks.onGift?.(gift);
  }

  /**
   * Handle incoming like (normalized from platform-specific data)
   */
  protected handleLike(like: StreamingLike): void {
    this.updateMetrics({
      likeCount: (this.streamMetrics.likeCount || 0) + like.likeCount
    });
    
    this.callbacks.onLike?.(like);
  }

  /**
   * Handle room info updates
   */
  protected handleRoomInfo(roomInfo: StreamingRoomInfo): void {
    this.roomInfo = roomInfo;
    this.callbacks.onRoomInfo?.(roomInfo);
  }

  /**
   * Create a platform-specific error
   */
  protected createPlatformError(message: string, code?: string, details?: any): PlatformError {
    const error = new Error(message) as PlatformError;
    error.platform = this.platform;
    error.code = code;
    error.details = details;
    return error;
  }

  /**
   * Handle platform errors
   */
  protected handleError(error: Error | PlatformError, context?: string): void {
    let platformError: PlatformError;
    
    if ('platform' in error) {
      platformError = error as PlatformError;
    } else {
      platformError = this.createPlatformError(
        context ? `${context}: ${error.message}` : error.message
      );
    }

    this.connectionState = 'error';
    this.callbacks.onError?.(platformError);
  }

  /**
   * Validate connection options
   */
  protected validateConnectionOptions(options: PlatformConnectionOptions): void {
    if (options.platform !== this.platform) {
      throw this.createPlatformError(
        `Platform mismatch: expected ${this.platform}, got ${options.platform}`
      );
    }

    if (!options.username?.trim()) {
      throw this.createPlatformError('Username is required');
    }
  }

  /**
   * Set connection timeout
   */
  protected setConnectionTimeout(timeoutMs: number = 10000): void {
    this.connectionTimeout = setTimeout(() => {
      if (this.connectionState === 'connecting') {
        this.handleError(
          this.createPlatformError('Connection timeout', 'TIMEOUT'),
          'Connection'
        );
        this.disconnect();
      }
    }, timeoutMs);
  }
}

